import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { currencyService } from '../services/currencyService';

const CurrencySelector = () => {
  const [currentCountry, setCurrentCountry] = useState(currencyService.getCountry());
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    return currencyService.subscribe(country => {
      setCurrentCountry(country);
    });
  }, []);

  const handleCountryChange = (country: string) => {
    currencyService.setCountry(country);
    setIsOpen(false);
  };

  const countryFlags: Record<string, string> = {
    'FR': '🇫🇷',
    'BE': '🇧🇪',
    'CH': '🇨🇭',
    'LU': '🇱🇺',
    'CA': '🇨🇦',
    'US': '🇺🇸',
    'GB': '🇬🇧',
    'AU': '🇦🇺',
    'NZ': '🇳🇿',
    'ZA': '🇿🇦',
    'MC': '🇲🇨',
    'DE': '🇩🇪',
    'AT': '🇦🇹',
    'DK': '🇩🇰',
    'FI': '🇫🇮',
    'IE': '🇮🇪',
    'IT': '🇮🇹',
    'NO': '🇳🇴',
    'ES': '🇪🇸',
    'PT': '🇵🇹',
    'NL': '🇳🇱',
    'SE': '🇸🇪',
    'AR': '🇦🇷',
    'BR': '🇧🇷',
    'CL': '🇨🇱',
    'CO': '🇨🇴',
    'MX': '🇲🇽',
    'PE': '🇵🇪',
    'UY': '🇺🇾'
  };

  const countryNames: Record<string, string> = {
    'FR': 'France (EUR)',
    'BE': 'Belgique (EUR)',
    'CH': 'Suisse (CHF)',
    'LU': 'Luxembourg (EUR)',
    'CA': 'Canada (CAD)',
    'US': 'États-Unis (USD)',
    'GB': 'Royaume-Uni (GBP)',
    'AU': 'Australie (AUD)',
    'NZ': 'Nouvelle-Zélande (NZD)',
    'ZA': 'Afrique du Sud (ZAR)',
    'MC': 'Monaco (EUR)',
    'DE': 'Allemagne (EUR)',
    'AT': 'Autriche (EUR)',
    'DK': 'Danemark (DKK)',
    'FI': 'Finlande (EUR)',
    'IE': 'Irlande (EUR)',
    'IT': 'Italie (EUR)',
    'NO': 'Norvège (NOK)',
    'ES': 'Espagne (EUR)',
    'PT': 'Portugal (EUR)',
    'NL': 'Pays-Bas (EUR)',
    'SE': 'Suède (SEK)',
    'AR': 'Argentine (ARS)',
    'BR': 'Brésil (BRL)',
    'CL': 'Chili (CLP)',
    'CO': 'Colombie (COP)',
    'MX': 'Mexique (MXN)',
    'PE': 'Pérou (PEN)',
    'UY': 'Uruguay (UYU)'
  };

  // Grouper les pays par région
  const regions = {
    'Europe': ['FR', 'BE', 'CH', 'LU', 'GB', 'DE', 'AT', 'DK', 'FI', 'IE', 'IT', 'NO', 'ES', 'PT', 'NL', 'SE', 'MC'],
    'Amérique du Nord': ['CA', 'US'],
    'Amérique du Sud': ['AR', 'BR', 'CL', 'CO', 'MX', 'PE', 'UY'],
    'Océanie & Afrique': ['AU', 'NZ', 'ZA']
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
      >
        <span>{countryFlags[currentCountry]}</span>
        <span>{currencyService.getCurrencyConfig().code}</span>
        <svg 
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-lg border border-gray-100 py-2 min-w-[250px] max-h-[400px] overflow-y-auto z-50"
        >
          {Object.entries(regions).map(([region, countryCodes]) => (
            <div key={region}>
              <div className="px-4 py-2 font-semibold text-sm text-gray-500 bg-gray-50">
                {region}
              </div>
              {countryCodes.map(code => (
                <button
                  key={code}
                  onClick={() => handleCountryChange(code)}
                  className={`w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 ${
                    currentCountry === code ? 'text-neon-orange font-medium' : ''
                  }`}
                >
                  <span>{countryFlags[code]}</span>
                  <span>{countryNames[code]}</span>
                  {currencyService.getCurrencyConfig(code)?.showKlarna && (
                    <span className="ml-auto text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      Klarna
                    </span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default CurrencySelector;